import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JsonService } from 'src/app/services/core/json.service';
import { BidApiResources } from '../sale/bid/bid.constants';
import {throwError as observableThrowError,  Observable, catchError, tap } from 'rxjs';
import { SearchApiResources } from './purchase-order.constants';
import { formatDate } from 'src/app/utils/utils.function';

@Injectable({
  providedIn: 'root'
})
export class PurchaseOrderService {
  private baseURL;

  constructor(
    private http: HttpClient,
    private jsonService: JsonService) {
    this.baseURL = SearchApiResources;
  }

  search(model: any): Observable<any> {
    let param=model
    this.formatePurchaseOrderDate(param)
    const endPoint = `${this.baseURL.search}`;
    return this.http.get<any>(endPoint, {params:param})
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  scopeSearch(model: any): Observable<any> {
    let param=model
    this.formatePurchaseOrderDate(param)
    const endPoint = `${this.baseURL.scopeSearch}`;
    return this.http.get<any>(endPoint, {params:param})
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  vendorSearch(model: any): Observable<any> {
    let param = { ...model };
    this.formatePurchaseOrderDate(param)
    // Convert jobids array to comma-separated string
  if (Array.isArray(param.jobids)) {
    param.jobids = param.jobids.join(',');
  }
    const endPoint = `${this.baseURL.vendorSearch}`;
    return this.http.get<any>(endPoint, {params:param})
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  generalVendorSearch(model: any): Observable<any> {
    let param = { ...model };
    const endPoint = `${this.baseURL.generalVendorSearch}`;
    return this.http.post<any>(endPoint, param)
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  vendorQuoteSearch(model: any): Observable<any> {
    let param = { ...model };

    // Extract vendor_id - it should be a single vendor ID
    const vendorId = Array.isArray(param.vendor_id) ? param.vendor_id[0] : param.vendor_id;

    if (!vendorId) {
      console.error('No vendor_id provided for vendor quote search');
      // Return empty observable to prevent errors
      return this.http.get<any>(`${this.baseURL.vendorQuoteSearch}`, {params:param})
                      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
    }

    // Use the existing vendor quote listing endpoint: vendor_quote/{vendor_id}/listing
    const endPoint = this.baseURL.vendorQuoteByVendor(vendorId);

    // Build query params for search, pagination
    const queryParams: any = {};
    if (param.search) queryParams.search = param.search;
    if (param.page) queryParams.page = param.page;
    if (param.per_page) queryParams.per_page = param.per_page;

    console.log('Fetching vendor quotes for vendor:', vendorId, 'with params:', queryParams);

    return this.http.get<any>(endPoint, {params: queryParams})
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  formatePurchaseOrderDate(param:any){
    if(param?.purchase_order_date_start){
      param.purchase_order_date_start=formatDate(param.purchase_order_date_start)
    }
    if(param?.purchase_order_date_end){
      param.purchase_order_date_end=formatDate(param.purchase_order_date_end)
    }
    if(param?.exclude_date){
      param.exclude_date=param.exclude_date?1:0
    }
  }

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

  errorHandlerMessage(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

  /**
   * Create a PO invitation for consolidation
   * @param purchaseOrderId - The PO ID
   * @param invitationData - The invitation details (startDate, deadline, reason)
   * @returns Observable with the created invitation
   */
  createPOInvitation(purchaseOrderId: string, invitationData: any): Observable<any> {
    const endPoint = this.baseURL.createInvitation(purchaseOrderId);
    return this.http.post<any>(endPoint, invitationData)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get PO invitation details
   * @param purchaseOrderId - The PO ID
   * @returns Observable with invitation details
   */
  getPOInvitation(purchaseOrderId: string): Observable<any> {
    const endPoint = this.baseURL.getInvitation(purchaseOrderId);
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Update PO invitation dates
   * @param purchaseOrderId - The PO ID
   * @param invitationId - The invitation ID
   * @param invitationData - The updated invitation details (startDate, deadline, reason)
   * @returns Observable with the updated invitation
   */
  updatePOInvitation(purchaseOrderId: string, invitationId: string, invitationData: any): Observable<any> {
    const endPoint = this.baseURL.updateInvitation(purchaseOrderId, invitationId);
    return this.http.put<any>(endPoint, invitationData)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Update PO status
   * @param purchaseOrderId - The PO ID
   * @param status - The new status ('active', 'open', 'closed', 'cancelled')
   * @returns Observable with the updated PO
   */
  updatePOStatus(purchaseOrderId: string, status: string): Observable<any> {
    const endPoint = this.baseURL.updateStatus(purchaseOrderId);
    return this.http.put<any>(endPoint, { status })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get open POs by vendor ID
   * @param vendorId - The vendor ID
   * @returns Observable with list of open POs
   */
  getOpenPOsByVendor(vendorId: string): Observable<any> {
    const endPoint = this.baseURL.getOpenPOsByVendor;
    return this.http.get<any>(endPoint, { params: { vendor_id: vendorId } })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Create consolidation request
   * @param poId - The PO ID
   * @param requestData - The request data (materials, job, dates, etc.)
   * @returns Observable with the created request
   */
  createConsolidationRequest(poId: string, requestData: any): Observable<any> {
    const endPoint = this.baseURL.createConsolidationRequest(poId);
    return this.http.post<any>(endPoint, requestData)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get consolidation requests by PO ID
   * @param poId - The PO ID
   * @returns Observable with list of requests
   */
  getConsolidationRequests(poId: string): Observable<any> {
    const endPoint = this.baseURL.getConsolidationRequests(poId);
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Get consolidation requests by Job ID
   * @param jobId - The Job ID
   * @returns Observable with list of requests
   */
  getConsolidationRequestsByJob(jobId: string): Observable<any> {
    const endPoint = this.baseURL.getConsolidationRequestsByJob(jobId);
    return this.http.get<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Approve consolidation request
   * @param requestId - The request ID
   * @returns Observable with the updated request
   */
  approveConsolidationRequest(requestId: string): Observable<any> {
    const endPoint = this.baseURL.approveConsolidationRequest(requestId);
    return this.http.put<any>(endPoint, {})
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Reject consolidation request
   * @param requestId - The request ID
   * @returns Observable with the updated request
   */
  rejectConsolidationRequest(requestId: string): Observable<any> {
    const endPoint = this.baseURL.rejectConsolidationRequest(requestId);
    return this.http.put<any>(endPoint, {})
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Remove consolidation request (revert approved to pending)
   * @param requestId - The request ID
   * @returns Observable with the result
   */
  removeConsolidationRequest(requestId: string): Observable<any> {
    const endPoint = this.baseURL.removeConsolidationRequest(requestId);
    return this.http.delete<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Merge approved consolidation requests into PO
   * @param poId - Purchase Order ID
   * @param requestIds - Optional array of specific request IDs to merge
   */
  mergeApprovedRequests(poId: string, requestIds?: string[]): Observable<any> {
    const endPoint = this.baseURL.mergeApprovedRequests(poId);
    const payload = requestIds ? { request_ids: requestIds } : {};
    return this.http.post<any>(endPoint, payload)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }

  /**
   * Delete consolidation requests by job and vendor
   * Used when user creates a new PO, removing any pending consolidation requests
   * @param jobId - Job ID
   * @param vendorId - Vendor ID
   */
  deleteConsolidationRequestsByVendor(jobId: string, vendorId: string): Observable<any> {
    const endPoint = this.baseURL.deleteConsolidationRequestsByVendor(jobId, vendorId);
    return this.http.delete<any>(endPoint)
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }
}
